import { FC, createContext, useContext, useCallback } from 'react';

import { useToast } from '@chakra-ui/react';

import { api } from '~/shared/services/api';

import { useTickets } from './tickets';
import { applyCascateTicketSubItemStatusChange } from '../utils/applyCascateTicketSubItemStatusChange';
import { OrderStatusEnum } from '../enums/OrderStatusEnum';

interface IActionsContextData {
  handleChangeTicketStatus: (
    ticketId: string,
    newStatus: OrderStatusEnum
  ) => Promise<void>;
  handleChangeTicketItemStatus: (
    ticketId: string,
    itemId: string,
    newStatus: OrderStatusEnum
  ) => Promise<void>;
  handleChangeTicketSubItemStatus: (
    itemId: string,
    newStatus: OrderStatusEnum,
    comboItemId: string,
    ticketId: string
  ) => Promise<void>;
}

const ActionsContext = createContext({} as IActionsContextData);

const ActionsProvider: FC = ({ children }) => {
  const toast = useToast({
    status: 'success',
    position: 'top-right',
  });

  const { tickets, changeTickets } = useTickets();

  const handleChangeTicketStatus = useCallback(
    async (ticketId: string, newStatus: OrderStatusEnum) => {
      if (newStatus === OrderStatusEnum.canceled) {
        await api.post(`/tickets/${ticketId}/cancel`);
        toast({
          status: 'success',
          description: 'Ticket cancelado com sucesso',
        });

        changeTickets((prevState) =>
          prevState.filter((ticket) => ticket.id !== ticketId)
        );

        return;
      }

      try {
        let newStatusReq = newStatus;
        if (newStatus === OrderStatusEnum.open) {
          const existItemWithProgressStatus = tickets
            .find((order) => order.id === ticketId)
            ?.items.some(
              (item) => item.status === OrderStatusEnum['in-progress']
            );

          if (existItemWithProgressStatus) {
            newStatusReq = OrderStatusEnum['in-progress'];
          }
        }

        await api.patch(`/tickets/${ticketId}`, {
          status: newStatusReq,
        });

        const statusText: Record<OrderStatusEnum, string> = {
          [OrderStatusEnum.open]: 'em aberto',
          [OrderStatusEnum.waiting]: 'em espera',
          [OrderStatusEnum.done]: 'pronto',
          [OrderStatusEnum.delivered]: 'entregue',
          [OrderStatusEnum['in-progress']]: 'em preparo',
          [OrderStatusEnum.canceled]: 'cancelado',
        };

        toast({
          title: `Ticket enviado para ${statusText[newStatus]}!`,
        });

        changeTickets((prevState) =>
          prevState.filter((ticket) => ticket.id !== ticketId)
        );
      } catch {
        toast({
          status: 'error',
          title: 'Ops, ocorreu um erro ao tentar alterar o status do ticket!',
        });
      }
    },
    [changeTickets, tickets, toast]
  );

  const handleChangeTicketItemStatus = useCallback(
    async (ticketId: string, itemId: string, newStatus: OrderStatusEnum) => {
      try {
        await api.patch(`/tickets/${ticketId}/items/${itemId}`, {
          status: newStatus,
        });

        changeTickets((prevState) =>
          prevState.map((ticket) => ({
            ...ticket,
            ...(ticket.id === ticketId && {
              items: ticket.items.map((item) => ({
                ...item,
                ...(item.id === itemId && {
                  status: newStatus,
                  subitems: applyCascateTicketSubItemStatusChange(
                    item.subitems,
                    newStatus
                  ),
                }),
              })),
            }),
          }))
        );
        toast({
          duration: 3000,
          title: `Item ${
            newStatus === OrderStatusEnum.done
              ? 'preparado'
              : newStatus === OrderStatusEnum.open
              ? 'reaberto'
              : 'alterado'
          }!`,
        });
      } catch {
        toast({
          status: 'error',
          title: 'Ops, ocorreu um erro ao tentar alterar o status do item!',
        });
      }
    },
    [changeTickets, toast]
  );

  const handleChangeTicketSubItemStatus = useCallback(
    async (
      itemId: string,
      newStatus: OrderStatusEnum,
      subItemId: string,
      ticketId: string
    ) => {
      try {
        await api.patch(
          `/tickets/${ticketId}/items/${itemId}/subitems/${subItemId}`,
          {
            status: newStatus,
          }
        );

        changeTickets((prevState) =>
          prevState.map((ticket) => ({
            ...ticket,
            ...(ticket.id === ticketId && {
              items: ticket.items.map((item) => ({
                ...item,
                ...(item.id === itemId && {
                  subitems: item.subitems.map((subitem) => ({
                    ...subitem,
                    ...(subitem.id === subItemId && {
                      status: newStatus,
                    }),
                  })),
                }),
              })),
            }),
          }))
        );

        toast({
          duration: 3000,
          title: `Subitem ${
            newStatus === OrderStatusEnum.done
              ? 'preparado'
              : newStatus === OrderStatusEnum.open
              ? 'reaberto'
              : 'alterado'
          }!`,
        });
      } catch {
        toast({
          status: 'error',
          title: 'Ops, ocorreu um erro ao tentar alterar o status do subitem!',
        });
      }
    },
    [changeTickets, toast]
  );

  return (
    <ActionsContext.Provider
      value={{
        handleChangeTicketStatus,
        handleChangeTicketItemStatus,
        handleChangeTicketSubItemStatus,
      }}
    >
      {children}
    </ActionsContext.Provider>
  );
};

const useActions = (): IActionsContextData => {
  return useContext(ActionsContext);
};

export { ActionsProvider, useActions };
